// ─── Spectral ────────────────────────────────────────────────────────────────

export interface SpectralRanges {
    uvC: number
    uvB: number
    uvA: number
    uv: number
    blue: number
    green: number
    red: number
    fr: number
    ir: number
    sum: number
    visibleSum: number
}

export interface SpectrogramPoint {
    waveLength: number
    irradiance: number
    pfd: number
}

export interface CsvDataPoint {
    waveLength: number
    value: number
}

// ─── DMX ─────────────────────────────────────────────────────────────────────

export type DmxResolution = 1 | 8 | 16 | 24 | 32

// ─── Lock / Link ─────────────────────────────────────────────────────────────

export interface RunningProjectLock {
    reason: 'RUNNING_PROJECT'
    projectId: string
    projectName: string
}

// ─── Zone ────────────────────────────────────────────────────────────────────

export interface ZoneDoc {
    _id: string
    name: string
    description: string
    parent: string
    order?: number
    virtual?: true
    virtualSource?: {
        zoneId: string
        type: 'mirror' | 'independent'
    }
    linkCount?: number
    linked?: { reason: 'HAS_CHILDREN' | 'HAS_LIGHT_CHANNEL' } | null
}

// ─── LinkedZone ───────────────────────────────────────────────────────────────

export interface ChannelMapEntry {
    sourceChannelId: string
    dmxAddress: number
    resolution: DmxResolution
}

export interface LinkedZoneDoc {
    _id: string
    name: string
    type: 'mirror' | 'independent'
    sourceZoneId: string
    targetZoneId: string
    channelMap: ChannelMapEntry[]
    enabled: boolean
    disabledReason: 'user' | 'mapping_error' | null
    createdAt: number
    updatedAt: number
}

// ─── LightChannel ────────────────────────────────────────────────────────────

export type LightChannelLinked =
    | { reason: 'LINKED_PROJECT_AND_SPECTRUM_MIX'; projectId: string; projectName: string; spectrumMixId: string; spectrumMixName: string }
    | { reason: 'UNUSED_SPECTRUM_HAS_LINKED_COLOR_CHANNEL'; projectId: string; projectName: string; spectrumMixId: string; spectrumMixName: string }
    | { reason: 'USED_IN_SPECTRUM_MIX'; spectrumMixId: string; spectrumMixName: string }

export interface LightChannelGroupLock {
    reason: 'USED_IN_SPECTRUM_MIX'
    spectrumMixId: string
    spectrumMixName: string
}

export interface LightChannelDoc {
    _id: string
    name: string
    description: string
    resolution: DmxResolution
    channel: number
    spectrogram: SpectrogramPoint[]
    zoneId: string | null
    csvData: CsvDataPoint[]
    spectrogramRanges: {
        pfd: SpectralRanges
        irradiance: SpectralRanges
    }
    sed: number
    groupLock: LightChannelGroupLock | null
    locked: RunningProjectLock | null
    linked: LightChannelLinked | null
}

// ─── LightRecipe ─────────────────────────────────────────────────────────────

export interface LightRecipeDoc {
    _id: string
    name: string
    description: string
    spectrogram: SpectrogramPoint[]
    spectrogramRanges: {
        pfd: SpectralRanges
        irradiance: SpectralRanges
    }
    csvData: CsvDataPoint[]
    sed: number
    locked: RunningProjectLock | null
    linked: { reason: 'LINKED_PROJECT'; projectId: string; projectName: string } | null
}

// ─── SpectrumMix ─────────────────────────────────────────────────────────────

export type SpectrumMixLinked =
    | { reason: 'LINKED_PROJECT'; projectId: string; projectName: string }
    | { reason: 'LINKED_PROJECT_AND_COLOR_CHANNEL'; lightChannelId: string; lightChannelName: string; projectId: string; projectName: string }

export interface SpectrumMixLightChannel {
    id: string
    intensity: number
    intensityPercent: number
    intensityRanges: {
        pfd: SpectralRanges
        irradiance: SpectralRanges
    }
}

export interface SpectrumMixDoc {
    _id: string
    name: string
    description: string
    zoneId: string
    lightChannels: SpectrumMixLightChannel[]
    lightRecipeId: string
    locked: RunningProjectLock | null
    linked: SpectrumMixLinked | null
}

// ─── GrowthRecipe ────────────────────────────────────────────────────────────

export const DARK_LIGHT_RECIPE_ID = '__dark__'

export interface CircadianEvent {
    lightRecipeId: string | null  // null = unconfigured (UI-only, never persisted); DARK_LIGHT_RECIPE_ID = all lights off
    hour: number
    minute: number
    sumLightsIntensity?: number
}

export interface SectionColor {
    background: string
    text: string
}

export interface RecipeSection {
    id: string
    name: string
    orderPosition: number
    repeatNum: number
    circadianLength: number
    circadianEvents: CircadianEvent[]
    sectionColor?: SectionColor
}

export interface GrowthRecipeDoc {
    _id: string
    name: string
    description: string
    sectionList: RecipeSection[]
    locked: RunningProjectLock | null
    linked: { reason: 'LINKED_PROJECT'; projectId: string; projectName: string } | null
}

// ─── Project ─────────────────────────────────────────────────────────────────

export interface SpectrogramMapItem {
    lightRecipeId: string
    spectrumMixId: string | null
}

export interface ProjectStatusChannel {
    channelId: number
    resolution: DmxResolution
    value: number
    fadeSeconds: number
    id: string
    name: string
    percent: number
    currentPfd: number
}

export interface ProjectSectionStatus {
    id: string
    name: string
    sectionTime: number
    currentSectionTime: number
    repeatNum: number
    currentRepeatNum: number
    circadianLength: number
    sectionPercent: string
}

export interface ProjectSectionShort {
    valid: boolean
    name?: string
    repeatNum?: number
    circadianLength?: number
}

export interface ProjectStatus {
    projectTime: number
    section: ProjectSectionStatus
    channels: ProjectStatusChannel[] | null
    nextSection: ProjectSectionShort
    prevSection: ProjectSectionShort
}

export interface PauseData {
    time: number
    pauseStatus: ProjectStatus
}

export interface ProjectDoc {
    _id: string
    name: string
    description: string
    zoneId: string
    recipeId: string
    spectrogramMap: SpectrogramMapItem[]
    running: boolean
    startTime: number | null
    pauseData: PauseData | null
    timeDelay: number
    errorCode: string
    locked: RunningProjectLock | null
    linked: null
    linkCount?: number
    linkErrorCount?: number
    mirrorLinkErrorCount?: number
    linkAllDisabled?: boolean
    independentSourceId?: string
    linkedZoneId?: string
}

// ─── System ──────────────────────────────────────────────────────────────────

export type DateFormat = 'YYYY-MM-DD' | 'MM/DD/YYYY' | 'DD/MM/YYYY'
export type TimeFormat = '24' | '12'

export interface DeviceSettings {
    deviceName: string
    cloudBackupEnabled: boolean
    lastCloudBackupAt?: number
    dateFormat: DateFormat | null
    timeFormat: TimeFormat | null
    offlineMode: boolean
    serverTime: number
    serialNumber?: string
    nightlyBuild?: boolean
    enableNightlyBuildInstalls?: boolean
}

export interface WifiApSettings {
    ssid: string
    passphrase: string
}


// ─── User ────────────────────────────────────────────────────────────────────

export interface UserDoc {
    _id: string
    userName: string
    password: string
    admin: boolean
    hash: string
}
